import Axios from '../config/Axios';
import loadBreadcrumb from './ActionBreadcrumb';
import { fnIsPresignedS3Url, fnShouldUseAuthenticatedRequest } from '../utils/urlRequestStrategy';

const HARD_LIMIT_BYTES = Number(process.env.REACT_APP_HARD_LIMIT_BYTES || 20 * 1024 * 1024);
const QUEUE_THRESHOLD_BYTES = Number(process.env.REACT_APP_QUEUE_THRESHOLD_BYTES || 10 * 1024 * 1024);
const FRONT_PREVIEW_TIMEOUT_MS = Number(process.env.REACT_APP_FRONT_PREVIEW_TIMEOUT_MS || 40000);

const CONVERTIBLE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'html', 'txt', 'ods', 'csv', 'rtf'];

function fnSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function fnBuildUnavailablePayload(cReason, cOriginalUrl = '', cOriginalFileName = '') {
  return {
    cType: 'unavailable',
    cReason: cReason || 'error',
    cOriginalUrl,
    cOriginalFileName,
    cContent: '',
  };
}

function fnExtractFileExtension(cFileName = '', cFileUrl = '') {
  const cSource = (cFileName || cFileUrl || '').split('?')[0].trim().toLowerCase();
  const cExt = cSource.includes('.') ? cSource.split('.').pop() : '';
  return cExt || '';
}

async function fnResolveRemoteFileSize(cUrl) {
  if (!cUrl || typeof cUrl !== 'string') return null;
  const bIsPresigned = fnIsPresignedS3Url(cUrl);
  const bUseAuth = fnShouldUseAuthenticatedRequest(cUrl);
  if (bIsPresigned || !bUseAuth) return null;

  const fnNormalizeHeaders = (oHeaders = {}) => {
    const oNormalized = {};
    Object.entries(oHeaders || {}).forEach(([cKey, cValue]) => {
      oNormalized[String(cKey).toLowerCase()] = cValue;
    });
    return oNormalized;
  };

  const fnParseContentLength = (oHeaders = {}) => {
    const nLen = Number(fnNormalizeHeaders(oHeaders)['content-length']);
    return Number.isFinite(nLen) && nLen > 0 ? nLen : null;
  };

  const fnParseContentRangeTotal = (oHeaders = {}) => {
    const cContentRange = fnNormalizeHeaders(oHeaders)['content-range'] || '';
    const oMatch = /\/(\d+)$/.exec(String(cContentRange));
    if (!oMatch?.[1]) return null;
    const nSize = Number(oMatch[1]);
    return Number.isFinite(nSize) && nSize > 0 ? nSize : null;
  };

  const fnShouldTryRangeByStatus = (nStatus) => {
    const nSafeStatus = Number(nStatus);
    if (!Number.isFinite(nSafeStatus)) return true;
    return nSafeStatus < 200 || nSafeStatus >= 300;
  };

  const fnHead = async () => {
    return Axios.head(cUrl, {
      timeout: 10000,
      validateStatus: (nStatus) => nStatus >= 200 && nStatus < 500,
    });
  };

  const fnRangeProbe = async () => {
    return Axios.get(cUrl, {
      timeout: 10000,
      responseType: 'arraybuffer',
      headers: { Range: 'bytes=0-0' },
      validateStatus: (nStatus) => nStatus >= 200 && nStatus < 500,
    });
  };

  let bMustTryRange = true;
  try {
    const oHead = await fnHead();
    const nHeadSize = fnParseContentLength(oHead?.headers || {});
    if (nHeadSize != null) return nHeadSize;
    bMustTryRange = fnShouldTryRangeByStatus(oHead?.status);
  } catch (oErr) {
    // ignored by design: fallback to range GET
    bMustTryRange = true;
  }

  if (bMustTryRange) {
    try {
      const oRange = await fnRangeProbe();
      const nRangeSize = fnParseContentRangeTotal(oRange?.headers || {});
      if (nRangeSize != null) return nRangeSize;
      const nFallbackLen = fnParseContentLength(oRange?.headers || {});
      if (nFallbackLen != null) return nFallbackLen;
    } catch (oErr) {
      // ignored by design
    }
  }

  return null;
}

function fnExtractAttachmentSize(oPayload = {}) {
  const aCandidates = [
    oPayload?.sizeBytes,
    oPayload?.cSize,
    oPayload?.nSize,
    oPayload?.bytes,
    oPayload?.contentLength,
    oPayload?.size,
    oPayload?.data?.sizeBytes,
    oPayload?.data?.cSize,
    oPayload?.data?.nSize,
    oPayload?.data?.bytes,
    oPayload?.data?.contentLength,
    oPayload?.data?.size,
  ];

  const nFound = aCandidates
    .map((v) => Number(v))
    .find((n) => Number.isFinite(n) && n > 0);

  return Number.isFinite(nFound) ? nFound : null;
}

function fnMapConvertErrorReason(oError) {
  const nStatus = Number(oError?.response?.status);
  const cCode = String(oError?.response?.data?.code || '').toLowerCase();
  const cMessage = String(oError?.response?.data?.message || oError?.message || '').toLowerCase();

  if (nStatus === 413 || cCode === 'too_large') return 'too_large';
  if (nStatus === 429 || nStatus === 503 || cCode === 'busy') return 'busy';
  if (nStatus === 504 || cCode === 'timeout' || oError?.code === 'ECONNABORTED') return 'timeout';
  if (cMessage.includes('timeout')) return 'timeout';
  return 'error';
}

function fnShouldRetryConvert(oError) {
  const nStatus = Number(oError?.response?.status);
  return [429, 502, 503, 504].includes(nStatus);
}

async function fnConvertToPdfWithFallback(cFileUrl, cFileName = '', nSizeHint = null) {
  const nSizeBytes = Number.isFinite(Number(nSizeHint)) && Number(nSizeHint) > 0
    ? Number(nSizeHint)
    : await fnResolveRemoteFileSize(cFileUrl);
  if (Number.isFinite(nSizeBytes) && nSizeBytes > HARD_LIMIT_BYTES) {
    return fnBuildUnavailablePayload('too_large', cFileUrl, cFileName);
  }

  const cConvertUrl = `${process.env.REACT_APP_CONVERT_URL}/convert`;
  if (!process.env.REACT_APP_CONVERT_URL) {
    return fnBuildUnavailablePayload('error', cFileUrl, cFileName);
  }

  let nAttempt = 0;
  while (nAttempt < 2) {
    try {
      const oFormData = new FormData();
      oFormData.append('file', cFileUrl);
      if (Number.isFinite(nSizeBytes) && nSizeBytes > QUEUE_THRESHOLD_BYTES) {
        oFormData.append('queued_hint', '1');
      }

      const oResponse = await Axios.post(cConvertUrl, oFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: FRONT_PREVIEW_TIMEOUT_MS,
      });

      const cPdfContent = oResponse?.data?.pdf_content;
      if (!cPdfContent) {
        return fnBuildUnavailablePayload('error', cFileUrl, cFileName);
      }

      return {
        cType: 'pdf',
        cContent: cPdfContent,
        cOriginalUrl: cFileUrl,
        cOriginalFileName: cFileName,
      };
    } catch (oError) {
      const cReason = fnMapConvertErrorReason(oError);
      nAttempt += 1;

      if (nAttempt < 2 && fnShouldRetryConvert(oError)) {
        await fnSleep(500 + Math.floor(Math.random() * 300));
        continue;
      }

      return fnBuildUnavailablePayload(cReason, cFileUrl, cFileName);
    }
  }

  return fnBuildUnavailablePayload('error', cFileUrl, cFileName);
}

export function confirmReading(nVersionId) {
  return Axios.post('/version/read', { ver_nid: nVersionId });
}

export function fileDocument(nDocumentId) {
  return Axios.put(`/file/${nDocumentId}`);
}

export function getDocumentDetails(nVersionId) {
  return Axios.get(`document/detail/${nVersionId}`).then(
    oResponse =>
      new Promise((resolve, reject) => {
        loadBreadcrumb(oResponse.data.oDocument.fldr_nid)
          .then(aBreadcrumbFolders => {
            resolve({
              ...oResponse.data,
              oDocument: oResponse.data.oDocument,
              oVersion: oResponse.data.oVersion,
              bCanEditInFolder: oResponse.data.bCanEditInFolder,
              bDocActive: oResponse.data.bDocActive,
              aBreadcrumbFolders
            });
          })
          .catch(oError => reject(oError));
      })
  );
}

export async function fetchAttachment(nVersionId) {
  return Axios.get(`document/detail/${nVersionId}/attachments`).then(
    oResponse =>
      new Promise((resolve) => {
        resolve({
          aAttachments: oResponse.data.aAttachments
        });
      })
  );
}

export async function fetchRequisites(nVersionId) {
  return Axios.get(`document/detail/${nVersionId}/requisites`).then(
    oResponse =>
      new Promise((resolve) => {
        resolve({
          aRequisites: oResponse.data.aRequisites
        });
      })
  );
}

export async function fetchVersion(nVersionId) {
  return Axios.get(`document/detail/${nVersionId}`).then(
    oResponse =>
      new Promise((resolve) => {
        resolve({
          ...oResponse.data,
          oDocument: oResponse.data.oDocument,
          oVersion: oResponse.data.oVersion,
          bCanEditInFolder: oResponse.data.bCanEditInFolder,
          bDocActive: oResponse.data.bDocActive
        });
      })
  );
}


async function fnDownloadFileByUrl(cAttachmentUrl, cFileName) {
  const bUseAuth = fnShouldUseAuthenticatedRequest(cAttachmentUrl) && !fnIsPresignedS3Url(cAttachmentUrl);

  let fBlob = null;
  if (bUseAuth) {
    const oResponse = await Axios.get(cAttachmentUrl, { responseType: 'blob' });
    fBlob = oResponse.data;
  } else {
    const oResponse = await fetch(cAttachmentUrl, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'follow',
    });
    if (!oResponse.ok) throw new Error('Falha ao baixar arquivo');
    fBlob = await oResponse.blob();
  }

  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(fBlob);
  a.download = cFileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

export function downloadPDF(nVersionId, cFileName, cFormat, cOrientation, cPreview, cServerPrint = 'L') {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem("cStorageToken");
    const needsCredentials = (cServerPrint === 'P') || (cServerPrint === 'N');

    let apiURL;
    let headers = {};

    if (needsCredentials) {
      headers = {
        "Authorization": `Bearer ${token}`,
        "Accept": "application/pdf"
      };
      apiURL = `${process.env.REACT_APP_CONVERT_URL}/printDocument/${nVersionId}/${cFormat}/${cOrientation}/${cPreview}`;
    } else {
      apiURL = `version/pdf/download/${nVersionId}/${cFormat}/${cOrientation}/${cPreview}`;
    }

    const axiosConfig = {
      responseType: 'blob',
      headers
    };

    if (needsCredentials) {
      axiosConfig.withCredentials = true;
    }

    Axios.get(apiURL, axiosConfig)
      .then((oResponse) => {
        const fFile = new Blob([oResponse.data], { type: oResponse.data.type });
        const cFileURL = URL.createObjectURL(fFile);
        const elLink = document.createElement('a');
        elLink.href = cFileURL;
        elLink.setAttribute('download', `${cFileName}.pdf`);
        document.body.appendChild(elLink);
        elLink.click();
        setTimeout(() => URL.revokeObjectURL(cFileURL), 100);
        resolve();
      })
      .catch(oError => {
        console.error('Erro no download do PDF:', oError);
        new Response(oError.response.data).text().then(cText => reject(cText));
      });
  });
}


export function fnGetLocalImageAttachment(nAttachmentId, cFileName) {
  return new Promise((resolve, reject) =>
    Axios.get(`attachment/preview/${nAttachmentId}`, { responseType: "blob" })
      .then(oResponse => {
        resolve({
          cContent: URL.createObjectURL(oResponse.data),
          cType: "blob"
        });
      })
      .catch(oError => new Response(oError.response.data).text().then(cText => reject(cText)))
  );
}

export function fnGetImageAttachment(nAttachmentId, cFileName, bIsLocal, cSource = 'attachment') {
  if (bIsLocal) {
    return fnGetLocalImageAttachment(nAttachmentId, cFileName);
  }

  let cEndpoint = '';
  switch (cSource) {
    case 'evidence':
      cEndpoint = `evidence/view/${nAttachmentId}`;
      break;
    case 'audit':
      cEndpoint = `audit-evidence/attachment/${nAttachmentId}`;
      break;
    case 'public':
      const hash = localStorage.getItem('sHash') || "";
      cEndpoint = `public/attachment/download/${hash}/${nAttachmentId}`;
      break;
    case 'attachment':
    default:
      cEndpoint = `attachment/preview/${nAttachmentId}`;
  }

  const fnHandleRemoteAttachment = async (cAttachmentUrl, cDocName = cFileName, oMeta = {}) => {
    if (!cAttachmentUrl) {
      return Promise.reject("Erro: URL não encontrada.");
    }

    const cCleanUrl = cAttachmentUrl.split('?')[0].trim();
    const cExt = fnExtractFileExtension(cDocName, cCleanUrl);
    const nAttachmentSize = fnExtractAttachmentSize(oMeta);
    if (CONVERTIBLE_EXTENSIONS.includes(cExt)) {
      return fnConvertToPdfWithFallback(cAttachmentUrl, cDocName, nAttachmentSize);
    }

    return { cContent: cAttachmentUrl, cType: 'url', cFileName: cDocName };
  };

  // Tratamento especial para audit
  if (cSource === 'audit') {
    return Axios.get(cEndpoint)
      .then(async (oResponse) => {
        const cAttachment = oResponse.data?.cAttachment;
        const cDocName = oResponse.data?.cDocName;
        return fnHandleRemoteAttachment(cAttachment, cDocName, oResponse.data);
      })
      .catch(() => Promise.reject("Erro ao buscar anexo da auditoria"));
  }

  // Fluxo padrão para blob ou JSON com URL
  return Axios.get(cEndpoint, { responseType: 'blob' })
    .then(async (oResponse) => {
      const contentType = oResponse.headers['content-type'];

      if (contentType.includes('application/json')) {
        const reader = new FileReader();
        reader.readAsText(oResponse.data);

        return new Promise((resolve, reject) => {
          reader.onload = () => {
            try {
              const jsonResponse = JSON.parse(reader.result);
              const cAttachmentUrl = jsonResponse.data?.cUrl;

              fnHandleRemoteAttachment(cAttachmentUrl, cFileName, jsonResponse).then(resolve).catch(reject);
            } catch (error) {
              reject("Erro ao processar JSON");
            }
          };
        });
      } else {
        const blobUrl = window.URL.createObjectURL(oResponse.data);
        return { cContent: blobUrl, cType: contentType };
      }
    })
    .catch(() => Promise.reject("Erro na obtenção do anexo"));
}


export function fnDownloadLocalAttachment(nAttachmentId, cFileName) {
  return new Promise((resolve, reject) =>
    Axios.get(`attachment/download/${nAttachmentId}`, {
      responseType: "blob"
    })
      .then(oResponse => {
        const fFile = new Blob([oResponse.data], { type: oResponse.data.type });
        const cFileURL = URL.createObjectURL(fFile);
        const elLink = document.createElement('a');
        elLink.href = cFileURL;
        elLink.setAttribute('download', `${cFileName}`);
        document.body.appendChild(elLink);
        elLink.click();
        resolve();
      })
      .catch(oError => new Response(oError.response.data).text().then(cText => reject(cText)))
  );
}

export function downloadAttachment(nAttachmentId, cFileName, cFileUrl = '', cSource = 'attachment') {
  return new Promise((resolve, reject) => {
    let cEndpoint = '';
    switch (cSource) {
      case 'evidence':
        cEndpoint = `evidence/view/${nAttachmentId}`;
        break;
      case 'audit':
        cEndpoint = `audit-evidence/attachment/${nAttachmentId}`;
        break;
      case 'attachment':
      default:
        cEndpoint = `attachment/download/${nAttachmentId}`;
    }

    Axios.get(cEndpoint)
      .then(oResponse => {
        // Para audit o retorno pode ser diferente
        const cAttachmentUrl =
          cSource === 'audit'
            ? oResponse.data?.cAttachment
            : oResponse.data?.data?.cUrl;

        if (!cAttachmentUrl) {
          return reject("Erro: URL não encontrada.");
        }

        if (cFileUrl !== '') {
          // Apenas retorna a URL
          resolve({ cUrl: cAttachmentUrl });
        } else {
          // Força download do arquivo original
          fnDownloadFileByUrl(cAttachmentUrl, cFileName)
            .then(() => resolve())
            .catch(() => reject('Erro ao baixar arquivo'));
        }
      })
      .catch(oError => {
        new Response(oError.response?.data).text().then(cText => reject(cText));
      });
  });
}




export function downloadZip(nVersionId, cFileName) {
  return new Promise((resolve, reject) =>
    Axios.get(`/version/zip/download/${nVersionId}`, { responseType: 'blob' })
      .then(oResponse => {
        const fFile = new Blob([oResponse.data], { type: oResponse.data.type });
        const cFileURL = URL.createObjectURL(fFile);
        const elLink = document.createElement('a');
        elLink.href = cFileURL;
        elLink.setAttribute('download', cFileName);
        document.body.appendChild(elLink);
        elLink.click();
        resolve();
      })
      .catch(oError => new Response(oError.response.data).text().then(cText => reject(cText)))
  );
}
